import { useCallback } from 'react';
import axios from 'axios';
import { ApolloError } from '@apollo/client';

import { snackbar } from '@occc-app/design-system';
import { getErrorMessage } from '@occc/utils';

import { usePromoCodesUploadSignedUrlGenerate } from './promocodes-upload.mutation.hook';

export type PromocodesUploadResult = {
  promoCodeGroupId: string;
  fileName: string;
};

export const usePromocodesUploadFile = () => {
  const [generateSignedUrl] = usePromoCodesUploadSignedUrlGenerate();

  const showErrorMessage = (error?: unknown) => {
    const message =
      typeof error === 'string'
        ? error
        : error
        ? getErrorMessage(error as ApolloError)
        : 'An error occurred while uploading the file';
    snackbar.error(message);
  };

  const uploadFileToSignedUrl = useCallback(
    async (file: File, signedUrl: string, fields: object) => {
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
      formData.append('file', file);
      return axios.post(signedUrl, formData);
    },
    [],
  );

  const uploadFile = useCallback(
    async (selectedFile: File): Promise<PromocodesUploadResult | null> => {
      try {
        const { data } = await generateSignedUrl({
          variables: {
            fileName: selectedFile.name,
          },
        });

        if (!data) {
          showErrorMessage();
          return null;
        }

        const { signedUrl, fields, promoCodeGroupId } = data.promoCodesUploadSignedUrlGenerate;

        if (Array.isArray(fields)) {
          showErrorMessage();
          return null;
        }

        await uploadFileToSignedUrl(selectedFile, signedUrl, fields);

        return {
          promoCodeGroupId,
          fileName: selectedFile.name,
        };
      } catch (error) {
        showErrorMessage(error);
        return null;
      }
    },
    [generateSignedUrl, uploadFileToSignedUrl],
  );

  return { uploadFile };
};
